import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Sparkles } from "lucide-react";
import { apiClient } from "@/lib/api";
import { SEO } from "@/components/seo/SEO";
import { Watermark } from "@/components/shared/Watermark";
import { MotionReveal, MotionStagger, MotionStaggerItem } from "@/components/shared/MotionReveal";
import { cn } from "@/lib/utils";
import { PAGE_HERO_IMAGES, INSIGHT_CATEGORY_IMAGES } from "@/constants/firmInfo";
import { useLanguage } from "@/i18n/LanguageContext";

const CATEGORIES = ["All", "Immigration", "Family Law", "Business Law", "Wills & Trusts"];

export default function InsightsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("All");
  const { t, isSpanish, getInsightOverride } = useLanguage();
  const categoryLabels = t("insightsList.categories");

  useEffect(() => {
    setLoading(true);
    const params = active !== "All" ? { category: active } : {};
    apiClient
      .get("/insights", { params })
      .then((res) => setItems(res.data))
      .finally(() => setLoading(false));
  }, [active]);

  const featured = active === "All" ? items[0] : null;
  const rest = featured ? items.slice(1) : items;

  const display = (post) => {
    const override = getInsightOverride(post.slug);
    return {
      title: override?.title || post.title,
      excerpt: override?.excerpt || post.excerpt,
    };
  };

  return (
    <>
      <SEO
        title={t("seo.insightsList.title")}
        description={t("seo.insightsList.description")}
        path="/insights"
      />

      <header className="relative bg-[color:var(--navy-950)] hero-noise overflow-hidden py-20 sm:py-24">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(11,18,32,0.93) 0%, rgba(11,18,32,0.87) 55%, rgba(11,18,32,0.97) 100%), url(${PAGE_HERO_IMAGES.insights})`,
            backgroundSize: "cover",
            backgroundPosition: "center 40%",
          }}
        />
        <Watermark word="Insights" position="center" className="text-white" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-400)]">{t("insightsList.eyebrow")}</span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-medium text-white text-balance">
            {t("insightsList.heading1")} <span className="italic text-[color:var(--gold-400)]">{t("insightsList.headingEmphasis")}</span>
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto text-base sm:text-lg">
            {t("insightsList.subtitle")}
          </p>
        </div>
      </header>

      <section className="py-14 sm:py-16 bg-[color:var(--paper)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="insights-category-tabs">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={cn(
                  "px-5 py-2 rounded-full text-sm font-medium border transition-colors duration-200",
                  active === cat
                    ? "bg-[color:var(--navy-950)] text-white border-[color:var(--navy-950)]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-[color:var(--navy-950)]"
                )}
                data-testid={`insights-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {categoryLabels[cat] || cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid place-items-center py-20">
              <div className="h-8 w-8 border-2 border-[color:var(--gold-500)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {featured && (
                <MotionReveal className="mb-12">
                  <Link
                    to={`/insights/${featured.slug}`}
                    className="group relative grid sm:grid-cols-5 rounded-[2rem] border border-slate-200 bg-[color:var(--navy-950)] overflow-hidden"
                    data-testid="insights-featured-card"
                  >
                    <div className="relative sm:col-span-3 p-8 sm:p-12">
                      <Watermark word="Featured" position="bottom-right" className="text-white" />
                      <div className="relative max-w-2xl">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--gold-400)]">
                          <Sparkles className="h-3.5 w-3.5" /> {t("insightsList.featuredBadge")} &middot; {isSpanish ? (categoryLabels[featured.category] || featured.category) : featured.category}
                        </span>
                        <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl text-white leading-snug group-hover:text-[color:var(--gold-400)] transition-colors">
                          {display(featured).title}
                        </h2>
                        <p className="mt-4 text-slate-300 leading-relaxed line-clamp-2">{display(featured).excerpt}</p>
                        <div className="mt-6 flex items-center gap-5 text-xs text-slate-400">
                          <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {featured.published_date}</span>
                          <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {featured.read_time_minutes} {t("insightsList.minRead")}</span>
                          <span className="inline-flex items-center gap-1 font-semibold text-[color:var(--gold-400)] group-hover:gap-2 transition-all">
                            {t("insightsList.readArticle")} <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="relative sm:col-span-2 min-h-[220px] hidden sm:block">
                      <img
                        src={INSIGHT_CATEGORY_IMAGES[featured.category] || INSIGHT_CATEGORY_IMAGES.Immigration}
                        alt={featured.category}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="eager"
                      />
                      <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[color:var(--navy-950)]/10 to-[color:var(--navy-950)]/60" />
                    </div>
                  </Link>
                </MotionReveal>
              )}

              <MotionStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="insights-grid">
                {rest.map((post) => (
                  <MotionStaggerItem key={post.slug}>
                    <Link
                      to={`/insights/${post.slug}`}
                      className="group flex flex-col h-full rounded-3xl border border-slate-200/70 bg-white p-6 shadow-[0_10px_30px_rgba(11,18,32,0.05)] hover:shadow-[0_18px_50px_rgba(11,18,32,0.12)] hover:-translate-y-1 transition-all duration-300"
                      data-testid="insight-preview-card"
                    >
                      <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.15em] text-[color:var(--navy-950)] bg-[color:var(--sand)] px-2.5 py-1 rounded-full w-fit">
                        {isSpanish ? (categoryLabels[post.category] || post.category) : post.category}
                      </span>
                      <h2 className="mt-4 font-heading text-lg text-[color:var(--navy-950)] leading-snug group-hover:text-[color:var(--gold-500)] transition-colors">
                        {display(post).title}
                      </h2>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1 line-clamp-3">{display(post).excerpt}</p>
                      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {post.published_date}</span>
                        <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.read_time_minutes} {t("insightsList.minRead")}</span>
                      </div>
                    </Link>
                  </MotionStaggerItem>
                ))}
                {!items.length && (
                  <p className="text-slate-500 col-span-full text-center py-10">{t("insightsList.noArticles")}</p>
                )}
              </MotionStagger>
            </>
          )}
        </div>
      </section>
    </>
  );
}
